/**
 * Health Status Indicator
 *
 * Compact visual indicator showing backend health status:
 * - Green: healthy
 * - Yellow: starting/degraded
 * - Red: down/unreachable
 * - Gray: checking
 */

import { memo } from 'react';
import { Wifi, WifiOff, Loader2, AlertCircle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useTranslation } from 'react-i18next';

interface HealthIndicatorProps {
  /** null = not yet checked, true = healthy, false = unhealthy */
  isHealthy: boolean | null;
  /** Backend is loading models */
  isStarting: boolean;
  /** Health check request in flight */
  isChecking: boolean;
  /** Compact mode (icon only) or full mode (icon + label) */
  compact?: boolean;
  /** Optional click handler */
  onClick?: () => void;
}

export const HealthIndicator = memo(function HealthIndicator({
  isHealthy,
  isStarting,
  isChecking,
  compact = false,
  onClick,
}: HealthIndicatorProps) {
  const { t } = useTranslation();

  const getStatus = (): {
    color: string;
    icon: React.ReactNode;
    label: string;
    bgColor: string;
  } => {
    if (isChecking && isHealthy === null) {
      return {
        color: 'text-neutral-500',
        bgColor: 'bg-neutral-100',
        icon: <Loader2 className="w-3 h-3 animate-spin" />,
        label: t('health.checking', 'Checking connection...'),
      };
    }

    if (isHealthy === false && !isStarting) {
      return {
        color: 'text-red-600',
        bgColor: 'bg-red-50',
        icon: <WifiOff className="w-3 h-3" />,
        label: t('health.offline', 'Offline'),
      };
    }

    if (isStarting) {
      return {
        color: 'text-amber-600',
        bgColor: 'bg-amber-50',
        icon: <AlertCircle className="w-3 h-3" />,
        label: t('health.starting', 'Server starting...'),
      };
    }

    if (isHealthy === true) {
      return {
        color: 'text-green-600',
        bgColor: 'bg-green-50',
        icon: <Wifi className="w-3 h-3" />,
        label: t('health.online', 'Online'),
      };
    }

    // Unknown state
    return {
      color: 'text-neutral-400',
      bgColor: 'bg-neutral-50',
      icon: <Loader2 className="w-3 h-3 animate-spin" />,
      label: t('health.unknown', 'Unknown'),
    };
  };

  const status = getStatus();

  const content = (
    <div
      className={cn(
        'inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium',
        status.bgColor,
        status.color,
        onClick && 'cursor-pointer hover:opacity-80 transition-opacity',
      )}
      onClick={onClick}
      role={onClick ? 'button' : 'status'}
      aria-label={`Backend status: ${status.label}`}
    >
      {status.icon}
      {!compact && <span>{status.label}</span>}
    </div>
  );

  return content;
});
